import React, { useEffect, useCallback, useState, useMemo } from 'react';

// @mui
import Table from '@mui/material/Table';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

// components
import formatCurrency from 'src/compass/shared/util/formatCurrency';
import { useExportOption } from 'src/compass/shared/util/useExportOption';
import defaultLabelFromField from 'src/compass/shared/util/defaultLabelFromField';
import { IGeneralObjectType } from 'src/compass/site/util';
import useRetrieveEntity from 'src/compass/shared/hooks/reactQuery/useRetrieveEntity';
import Iconify from 'src/components/iconify';
import CasTableToolbar, { ICasTableToolbar } from './CasTableToolbar';
import CasTableHead from './CasTableHead';
import CasTableBody, { TRowSx } from './CasTableBody';
import CasTableFooter from './_internal/CasTableFooter';
import ICasTableColumn from './ICasTableColumn';
import CasDisplayField from '../CasDisplayField/CasDisplayField';
import useCasTable from './_internal/useCasTable';
import AppSettings from '../../util/appSettings';
import CasTablePagination from './_internal/CasTablePagination';
import ICasRenderCellProps from './ICasRenderCellProps';
import CasScrollbar from '../CasScrollbar/CasScrollbar';
import CasTableContainer from './CasTableContainer';

export interface ICasTable
  extends Omit<ICasTableToolbar, 'onFilterChange' | 'buttons' | 'currentFilterValues'> {
  entity?: string;
  data?: IGeneralObjectType;
  toolbarButtons?: React.ReactNode[];
  belowToolbar?: React.ReactNode | ((data: any) => React.ReactNode);
  refreshBtn?: boolean;
  autoRefresh?: boolean;
  columns: ICasTableColumn[];
  columnsSortable?: boolean;
  includeExport?: boolean;
  update?: Date | null;
  showPagination?: boolean;
  defaultOrder?: 'asc' | 'desc';
  defaultOrderBy?: string;
  defaultRowsPerPage?: number;
  smallAsCard?: boolean;
  rowSx?: TRowSx;
  dataRowsNowrap?: boolean;
  //  Share response data with parent
  setIsPending?: React.Dispatch<React.SetStateAction<boolean>>;
  setRequestMetadata?: React.Dispatch<React.SetStateAction<{ url: string, params: IGeneralObjectType }>>;
  setData?: React.Dispatch<React.SetStateAction<any>>;

  additionalPayload?: IGeneralObjectType;
  headerDisplayFields?: { label: string; name: string; type?: string }[];
  initialLoad?: boolean;
  rowChildrenColumns?: ICasTableColumn[];
}

interface Option {
  value: string;
  label: string;
}

//  Function to find the first object without sortable:true and return its column name
function findFirstSortableColumn(columnArray: any[]) {
  const nonSortableObj = columnArray.find((obj) => obj.sortable !== false);
  if (nonSortableObj) return nonSortableObj.field;
  return null;
}

/*
 To use the ExpandChildrenColumn feature, make sure:
  1. The data must contain a "subMenus" array on each row — this will be shown as the expandable children.
  2. Pass "rowChildrenColumns" to define how each item in the "subMenus" array should be rendered.
*/

export const ExpandChildrenColumn = {
  render: ({ onToggleExpand, isExpanded }: ICasRenderCellProps) => (
    <IconButton aria-label="expand row" size="small" onClick={onToggleExpand}>
      <Iconify
        icon={isExpanded ? 'ep:arrow-up-bold' : 'ep:arrow-down-bold'}
        width={16}
        color="#808080"
      />
    </IconButton>
  ),
};

export const CasTableStyles = {
  subHeaderStyles: () => ({ fontWeight: 'bold', backgroundColor: '#c1d5c5' }),
}

function filterColumns(defaultColumns: any[], filtersData: IGeneralObjectType): any[] {
  console.log("filtersData", filtersData);
  return defaultColumns.filter(col => {
    if (typeof col.hiddenOnFilter === 'function') return !col.hiddenOnFilter(filtersData); // Show column if the function returns FALSE (meaning it should NOT be hidden)
    if (col.hiddenOnFilter && filtersData[col.field]) return false; // Hide the hiddenOnFilter column if it is being filtered
    return true; // Otherwise, show the column
  });
};

export default function CasTable({
  entity,
  dataRowsNowrap= false,
  data: dataFromParent,
  columns: parmColumns,
  rowChildrenColumns = [],
  columnsSortable = true,
  filters,
  toolbarButtons = [],
  belowToolbar: parmBelowToolbar,
  refreshBtn,
  autoRefresh = false,
  includeExport = true,
  options,
  update = null,
  showPagination = true,
  defaultOrder = 'asc',
  defaultOrderBy: parmDefaultOrderBy,
  defaultRowsPerPage,
  smallAsCard= false,
  additionalPayload = {},
  rowSx,
  setData,
  setRequestMetadata,
  setIsPending,
  headerDisplayFields,
  initialLoad = true
}: ICasTable) {
  if (entity && dataFromParent) throw Error("Can't specify both entity and data.");
  if (!entity && !dataFromParent) throw Error('Either entity or data must be specified.');

  const defaultColumns = useMemo(
    () =>
      parmColumns.map(({field, id, label, sortable, ...rest}) => ({
        ...rest,
        field,
        id: id || field,
        label: label || defaultLabelFromField(field),
        sortable: entity && columnsSortable && field ? sortable : false,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parmColumns]
  );
  
  // dynamically find the column to sort
  const defaultOrderBy = useMemo(
    () => parmDefaultOrderBy ?? findFirstSortableColumn(parmColumns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parmColumns]
  );
  const table = useCasTable({ defaultOrderBy, defaultOrder, defaultRowsPerPage });

  const [casTableData, setCasTableData] = useState<any>(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const [loadEnabled, setLoadEnabled] = useState(initialLoad);
  const [columns, setColumns] = useState(defaultColumns);
  
  const isAutoRefreshingEnabled = autoRefresh && refreshCount < AppSettings.autoRefreshMax();

  const formattedData = () => {
    const { filters: filterValues, ...rest }: any = casTableData || {};
    return { ...rest, ...filterValues, ...additionalPayload };
  }

  const {
    data: dataFromQuery,
    error,
    isPending,
    isFetching,
    isRefetching,
    refetch,
  } = useRetrieveEntity({
    entity: entity ?? null,
    params: formattedData(),
    enabled: !!casTableData && !dataFromParent && loadEnabled,
    refetchInterval: autoRefresh && isAutoRefreshingEnabled ? (AppSettings.autoRefreshSeconds() * 1000) : false,
  });

  const data = dataFromParent ?? dataFromQuery;

  useEffect(() => {
    if(isRefetching) setRefreshCount((prevCount) => prevCount + 1);
    
    
    
    /*
    TODO Probably should have:  Else reset count to zero.  
    i.e. when doing a normal fetch (!isRefetching), reset counter to zero

    This would reset the counter to zero when user interacts with the component (e.g. filter, sort or pagination etc)

    setRefreshCount((prevCount) => isRefetching ? prevCount + 1 : 0);
    */



  },[isRefetching])

  useEffect(() => {
    if (update) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update]);

  useEffect(() => {
    if (!!casTableData && autoRefresh) {
      /*  If autoRefresh is stopped & re-enabled by parent, 
          do an immediate refetch & reset counter.

          Note: !!casTableData prevents refetch on initial component load
      */
      refetch();
      setRefreshCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  useEffect(() => {
    // Notify parent of updated raw data
    if (setData && !isPending) setData(data);
    if(setIsPending) setIsPending(isPending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isPending]);

  useEffect(() => {
    if (setRequestMetadata) {
      setRequestMetadata({
        params: formattedData(),
        url: `${process.env.REACT_APP_API_URL}${entity}/List`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casTableData]);
  
  useEffect(() => {
    const tableData: any = {};
    const filtersData: { [key: string]: string | boolean | Option | null } = {};
    filters?.forEach((filter: any) => {
      filtersData[filter.name] = filter.initialValue;
    });
    tableData.filters = filtersData || {};

    setColumns(() => filterColumns(defaultColumns, filtersData));

    if (showPagination && entity) {
      tableData.pageNumber = table.page + 1;
      tableData.pageSize = table.rowsPerPage;
    }
    if (defaultOrderBy) {
      tableData.sortColumn = table.orderBy;
      tableData.sortOrder = table.order;
    }
    setCasTableData(tableData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showPagination && casTableData && entity) {
      const pageNum = table.page + 1;
      if (casTableData.pageNumber !== pageNum || casTableData.pageSize !== table.rowsPerPage) {
        setCasTableData((prev: any) => ({
          ...prev,
          pageNumber: table.page + 1,
          pageSize: table.rowsPerPage,
          sortColumn: table.orderBy,
          sortOrder: table.order,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.page, table.rowsPerPage]);

  useEffect(() => {
    if (defaultOrderBy && casTableData) {
      if (casTableData.sortColumn !== table.orderBy || casTableData.sortOrder !== table.order) {
        if (table.page !== 0) {
          const newPage = 0;
          table.onChangePage(null, newPage);
        } else
          setCasTableData((prev: any) => ({
            ...prev,
            sortColumn: table.orderBy,
            sortOrder: table.order,
          }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.orderBy, table.order]);

  const handleFilters = useCallback(
    (name: string, value: string) => {
      table.onResetPage();
      // check if column contains onChange, to uplift the value.
      const filterObject = filters.find((filter: any) => filter.name === name);
      filterObject?.onChange?.(value);

      // Column hide on filter value will only work, if column field name is same as that of filter name.
      setColumns(() => filterColumns(defaultColumns, { 
        ...casTableData.filters, 
        [name]: value 
      }));

      setLoadEnabled(true);
      setCasTableData((prevState: any) => ({
        ...prevState,
        filters: { ...prevState.filters, [name]: value },
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, casTableData?.filters]
  );

  const { exportOption } = useExportOption({
    entity,
    params: formattedData(),
    includeExport: entity ? includeExport : false,
  });

  const finalOptions = options ? [...exportOption, ...options] : exportOption;

  const displayFieldComponents = headerDisplayFields?.map(({ label, name, type }, index) =>
    data && name in data ? (
      <CasDisplayField
        key={index}
        label={label}
        value={type === 'Currency' ? formatCurrency(data[name]) : data[name]}
      />
    ) : null
  );
  if (displayFieldComponents) {
    toolbarButtons = [...displayFieldComponents, ...toolbarButtons];
  }

  const belowToolbar  = useMemo(() => {
    if (typeof parmBelowToolbar  === 'function') {
      return parmBelowToolbar (data);
    }
  
    return parmBelowToolbar  ?? null;
  }, [parmBelowToolbar , data]);

  const handleReenableAutorefreshClick = () => {
    refetch();
    setRefreshCount(0);
  }


  return (
    <div className={`${smallAsCard ? "table-list-card": ""} ${dataRowsNowrap ? "table-tbody-nowrap": ""}`}>
      <CasTableToolbar
        buttons={toolbarButtons}
        refreshBtn={refreshBtn}
        refetch={refetch}
        filters={filters}
        currentFilterValues={casTableData?.filters || {}}
        onFilterChange={handleFilters}
        options={finalOptions}
      />
      {belowToolbar}
      {autoRefresh && !isRefetching && !isAutoRefreshingEnabled && (
        <Typography variant="body1" onClick={handleReenableAutorefreshClick} className="auto-refresh-text">
          Auto-refresh is disabled. Click here to enable
        </Typography>
      )}

      <CasTableContainer>
        <CasScrollbar autoHide={false} showTopScrollbar>
          <Table size="medium">
            <CasTableHead
              headLabel={columns}
              rowCount={data?.result?.length}
              onSort={table.onSort}
              order={table.order}
              orderBy={table.orderBy}
            />

            {(loadEnabled || data) &&
              <>
                <CasTableBody
                  loading={isFetching}
                  columns={columns}
                  rowData={data?.result}
                  error={error?.message ?? ''}
                  rowSx={rowSx}
                  rowChildrenColumns={rowChildrenColumns}
                />
                <CasTableFooter rowData={data?.result} totals={data?.totals} columns={columns} />
              </>
            }
          </Table>
        </CasScrollbar>
      </CasTableContainer>
      {showPagination && entity && (
        <CasTablePagination
          count={data?.totalCount || 0}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
        />
      )}
    </div>
  );
}
